import type {
    RibbonTabDefinitionBase,
    RibbonTabDefinitionSLR,
    RibbonTabDefinitionMLR,
} from 'owa-acui';
import { assertNever } from 'owa-assert';

/**
 * In popouts, the `tab.id` is formatted as `<TabId>_<EditorId>`, where `TabId` is the normal enum value and `EditorId` is a unique identifier for the compose session.
 * For the purpose of determining "matching tabs", we only care about the normal `TabId` enum value.
 */
const getCoreTabId = (id: string): string => id.split('_')[0];

/**
 * Returns the selected tab based on the provided parameters.
 * @param tabs The array of ribbon tabs.
 * @param selectedTabId The currently selected tab id.
 * @param defaultSelectedTabId The default tab id to select if no match is found.
 * @param onTabHeaderClick Callback function to call when a tab header is clicked.
 *                          This is used to ensure the selected tab is set correctly in the UI.
 *                          It is called when no matching tab is found and the first tab is selected.
 * @returns the matching tab
 */
export function getSelectedTab(
    tabs: RibbonTabDefinitionBase[],
    selectedTabId: string,
    defaultSelectedTabId: string,
    onTabHeaderClick: (tabId: string) => void
): RibbonTabDefinitionSLR | RibbonTabDefinitionMLR {
    // Get the tab with the same id as the selectedTabId
    let matchingTabs: RibbonTabDefinitionBase[] = tabs.filter(
        tab => getCoreTabId(tab.id) === getCoreTabId(selectedTabId)
    );

    // If there is no match for the tab, check if the default selected tab matches
    if (matchingTabs.length === 0 && selectedTabId !== defaultSelectedTabId) {
        matchingTabs = tabs.filter(
            tab => getCoreTabId(tab.id) === getCoreTabId(defaultSelectedTabId)
        );
    }

    // If there is no match for the tab, return the first tab
    const matchingTab: RibbonTabDefinitionBase | undefined =
        matchingTabs.length === 0 ? tabs[0] : matchingTabs[0];

    if (matchingTabs.length === 0 && matchingTab?.id) {
        onTabHeaderClick(matchingTab.id);
    }

    if (matchingTab) {
        return matchingTab as RibbonTabDefinitionSLR | RibbonTabDefinitionMLR;
    } else {
        assertNever(matchingTab as never);
    }
}
